/** Abandoned carts (GET abandoned-carts): carts left without an order; recovery email (POST abandoned-carts/{id}/recovery-email, once per 24 h) + auto reminder (shop-ext abandoned). */
import React, { useCallback, useEffect, useState } from 'react';
import { Avatar, Button, Card, Checkbox, Empty, InputNumber, Result, Select, Space, Switch, Table, Tag, Tooltip, Typography, message } from 'antd';
import { MailOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import { QUIET } from './api';
import { Hint, fmtDate, listOf, money, toastErr, totalOf, useSite } from './common';
import { useShopExt } from './ShopExtSettings';

const { Text } = Typography;

const ShopAbandoned = () => {
    const { t } = useTranslation();
    const { api } = useSite();
    const [f, setF] = useState({ hours: 1, with_email: true, page: 1 });
    const [data, setData] = useState(null);
    const [soon, setSoon] = useState(false);
    const load = useCallback(async () => {
        try {
            const d = await api.get('/abandoned-carts', { hours: f.hours, with_email: f.with_email ? 1 : undefined, page: f.page, per_page: 25 }, QUIET);
            setData({ items: listOf(d), total: totalOf(d) });
        } catch (e) { if (e?.status === 404) setSoon(true); else toastErr(e); setData({ items: [], total: 0 }); }
    }, [api, f]);
    useEffect(() => { load(); }, [load]);

    const ext = useShopExt();
    const [ab, setAb] = useState({ auto_remind: false, hours: 4 });
    useEffect(() => { if (ext.s?.abandoned) setAb(ext.s.abandoned); }, [ext.s]);
    const saveAb = async (patch) => {
        const next = { ...ab, ...patch };
        setAb(next);
        if (await ext.save({ abandoned: next })) message.success(t('Saved'));
    };
    const [sending, setSending] = useState(null);
    const sendRecovery = async (c) => {
        setSending(c.cart_id);
        try {
            const r = await api.post(`/abandoned-carts/${c.cart_id}/recovery-email`, {}, QUIET);
            message.success(t('Recovery email sent'));
            setData((d) => ({ ...d, items: d.items.map((x) => (x.cart_id === c.cart_id ? { ...x, recovery_sent_at: r?.recovery_sent_at || new Date().toISOString(), can_send_recovery: false } : x)) }));
        } catch (e) { toastErr(e); } finally { setSending(null); }
    };

    if (soon) return <Card><Result status="info" title={t('Abandoned carts are coming soon')} /></Card>;
    return (
        <Card size="small" title={t('Abandoned carts')}>
            <Space wrap style={{ marginBottom: 12 }}>
                <Select value={f.hours} onChange={(hours) => setF({ ...f, hours, page: 1 })} style={{ width: 200 }}
                    options={[1, 6, 24, 72].map((h) => ({ value: h, label: t('Idle for {{n}}+ hours', { n: h }) }))} />
                <Checkbox checked={f.with_email} onChange={(e) => setF({ ...f, with_email: e.target.checked, page: 1 })}>{t('With email only')}</Checkbox>
                {ext.s && (
                    <Space size={6}>
                        <Switch size="small" checked={!!ab.auto_remind} onChange={(v) => saveAb({ auto_remind: v })} />
                        <Text>{t('Auto reminder after')}</Text>
                        <InputNumber size="small" min={1} max={168} value={ab.hours} disabled={!ab.auto_remind} onChange={(v) => setAb({ ...ab, hours: v || 4 })} onBlur={() => saveAb({})} addonAfter={t('hours')} style={{ width: 120 }} />
                        <Hint title={t('One reminder email per cart, with a link that restores it')} />
                    </Space>
                )}
            </Space>
            <Table rowKey="cart_id" size="small" loading={!data} dataSource={data?.items} scroll={{ x: 760 }}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No abandoned carts')} /> }}
                pagination={{ current: f.page, pageSize: 25, total: data?.total, hideOnSinglePage: true, onChange: (page) => setF({ ...f, page }) }}
                columns={[
                    { title: t('Shopper'), key: 's', render: (_, c) => <div><Text>{c.name || c.email || t('Anonymous')}</Text>{c.name && c.email && <div><Text type="secondary" style={{ fontSize: 12 }}>{c.email}</Text></div>}{c.reached_checkout && <Tag color="orange" style={{ marginTop: 2 }}>{t('Reached checkout')}</Tag>}</div> },
                    {
                        title: t('Items'), key: 'i', render: (_, c) => (
                            <Avatar.Group max={{ count: 4 }} size="small">
                                {listOf(c.items).map((i, k) => <Tooltip key={k} title={`${i.title} × ${i.quantity}`}><Avatar shape="square" src={i.image?.src}>{(i.title || '?')[0]}</Avatar></Tooltip>)}
                            </Avatar.Group>
                        ),
                    },
                    { title: t('Value', { context: 'cart' }), key: 'v', width: 120, align: 'right', render: (_, c) => <>{money(c.total, c.currency)}{c.discount_code && <div><Tag style={{ marginInlineEnd: 0 }}>{c.discount_code}</Tag></div>}</> },
                    { title: t('Last activity'), dataIndex: 'updated_at', width: 160, render: (v) => fmtDate(v) },
                    {
                        title: '', key: 'a', width: 170, render: (_, c) => (c.email ? (
                            <Space direction="vertical" size={0}>
                                <Button size="small" icon={<MailOutlined />} loading={sending === c.cart_id} disabled={c.can_send_recovery === false} onClick={() => sendRecovery(c)}>{t('Send recovery email')}</Button>
                                {c.recovery_sent_at && <Text type="secondary" style={{ fontSize: 12 }}>{t('Sent {{time}}', { time: fmtDate(c.recovery_sent_at) })}</Text>}
                            </Space>
                        ) : null),
                    },
                ]} />
        </Card>
    );
};

export default ShopAbandoned;
